<script setup>
import { useI18n } from 'vue-i18n'
import { defineProps, ref } from 'vue'

const { t } = useI18n({ useScope: 'global' })

const props = defineProps({
  logs: Array,
  humanId: String,
  carInfo: String
})

const expandedLogId = ref(null);

function toggleLog(logId) {
  if (expandedLogId.value === logId) {
    expandedLogId.value = null;
  } else {
    expandedLogId.value = logId;
  }
}

function getClassForLogType(type) {
  switch (type) {
    case 'create': return 'table-success'
    case 'update': return 'table-warning'
    case 'delete': return 'table-dark'
    case 'edit_order': return 'table-primary'
    case 'upload': return 'table-info'
    case 'error': return 'table-danger'
    default: return ''
  }
}

function tryParseArray(str) {
  try {
    const parsed = JSON.parse(str)
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

function noteSegments(log) {
  const segments = []
  if (log.userUsername) segments.push({ kind: 'text', value: `${t('logwords.user')} ${log.userUsername}` })

  const order = orderSegment(log)
  if (order) segments.push(order)

  if (log.clientId) segments.push({ kind: 'text', value: `${t('logwords.client')} ${log.clientId}` })
  if (log.fileId) segments.push({ kind: 'text', value: `${t('logwords.file')} ${log.fileId}` })
  return segments
}

function orderSegment(log) {
  if (!log.orderTitle && !log.orderHumanId && !props.humanId && !log.orderId) return null

  let text
  if (log.orderTitle) {
    text = log.orderTitle
  } else {
    const carInfo = log.orderCarInfo || props.carInfo || ''
    text = carInfo ? `${t('logwords.order')} ${carInfo}` : t('logwords.order')
  }

  const badge = log.orderHumanId || props.humanId || log.orderId || ''
  return { kind: 'order', text, badge: badge ? String(badge) : '' }
}

function localizeDescription(log) {
  return log.description || ''
}

function getLocalizedFieldName(field) {
  const translationKey = `log.fields.${field}`;
  const translation = t(translationKey);
  return translation !== translationKey ? translation : field;
}

const legacyStatusMap = {
  'Нове замовлення': 'active',
  'В роботі': 'work',
  'Активне': 'active',
  'На порізці': 'cutting',
  'Виконано': 'done',
  'Закрито': 'closed'
};

function getLocalizedValue(value) {
  if (value === true || value === 'true') return t('log.yes');
  if (value === false || value === 'false') return t('log.no');

  if (typeof value === 'string') {
    const mappedKey = legacyStatusMap[value] || value;

    const statusTranslationKey = `statuses.${mappedKey}`;
    const statusTranslation = t(statusTranslationKey);

    if (statusTranslation !== statusTranslationKey) {
      return statusTranslation;
    }
  }
  return value;
}
</script>

<template>
  <table class="table mt-4 table-bordered">
    <thead>
    <tr>
      <th>#</th>
      <th>{{ t('tableheadings.type') }}</th>
      <th>{{ t('tableheadings.user') }}</th>
      <th>{{ t('tableheadings.note') }}</th>
      <th>{{ t('tableheadings.date') }}</th>
    </tr>
    </thead>
    <tbody>
    <template v-for="(log, index) in props.logs" :key="log._id">
      <tr :class="getClassForLogType(log.type)">
        <th>{{ index + 1 }}</th>
        <td style="max-width: 250px;">
          {{ t('operations.' + log.type) }}
          <template v-for="(seg, i) in noteSegments(log)" :key="i">
            <span>{{ i === 0 ? ' - ' : ' | ' }}</span>
            <template v-if="seg.kind === 'order'">
              <span v-if="seg.text">{{ seg.text }}</span>
              <span v-if="seg.badge" class="badge bg-secondary ms-1">{{ seg.badge }}</span>
            </template>
            <span v-else>{{ seg.value }}</span>
          </template>
        </td>
        <td>{{ log.systemUser?.username }}</td>
        <td>
          <button v-if="log.type === 'edit_order' && log.description" class="btn btn-info btn-sm" type="button"
                  @click="toggleLog(log._id)">
            {{ expandedLogId === log._id ? t('buttons.hide') : t('buttons.show') }}
          </button>
        </td>
        <td>{{ log.updatedAt }}</td>
      </tr>

      <tr v-if="expandedLogId === log._id">
        <td colspan="5" class="p-0">
          <Transition name="collapse">
            <div class="p-3 bg-light border-start-0 border-end-0">
              <template v-for="(change, idx) in tryParseArray(localizeDescription(log))" :key="idx">
                <div class="log-change-item mb-2 small">
                  <strong>{{ getLocalizedFieldName(change.field) }}:</strong>
                  <div class="d-flex flex-column ps-2">
                        <span class="text-danger">
                          <del>{{ t('logwords.was') }} {{ getLocalizedValue(change.before) }}</del>
                        </span>
                    <span class="text-success">
                          {{ t('logwords.became') }} {{ getLocalizedValue(change.after) }}
                        </span>
                  </div>
                </div>
              </template>
            </div>
          </Transition>
        </td>
      </tr>
    </template>
    </tbody>
  </table>
</template>

<style scoped lang="scss">
.collapse-enter-active {
  transition: all 0.3s ease-out;
}

.collapse-leave-active {
  transition: all 0.3s ease-in;
}

.collapse-enter-from,
.collapse-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}


.log-change-item strong {
  display: block;
  margin-bottom: 4px;
  color: #555;
}
</style>